import { useMemo } from 'react';
import { compact } from 'lodash-es';
import { Accounting } from 'anchor-scout-common/types';
import { useStore } from '~/contexts';
import { useQboClasses } from '~/hooks/reactQuery/queries/useQboClasses';
import { useLocaleKeys } from '~/i18n';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { AdvancedSetting } from '../../ServiceAdvancedSettings.types';
const accountingName = getAccountingDetails(Accounting.QBO).fullName;
export const useGetSettings = (param)=>{
    let { isFreshlyAdded, isApprovedRelationship, service, now, effectiveDate } = param;
    const { t: optionNamesTexts } = useLocaleKeys('components.serviceAdvancedSettings.optionNames');
    const { t: settingsTexts } = useLocaleKeys('components.serviceAdvancedSettings.settings');
    const { t: advancedSettingsTexts } = useLocaleKeys('components.serviceAdvancedSettings');
    const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
    const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const { qboClasses, isLoading: isLoadingQboClasses } = useQboClasses();
    const allowToCustomizeQboClasses = useStore(selectors.integration.allowToCustomizeQboClasses);
    return useMemo(()=>{
        const isDraft = !isApprovedRelationship;
        const isFreshlyAddedOrDraft = isDraft || isFreshlyAdded;
        const isOngoing = serviceBillingModel.isOngoing(serviceModel.billingTrigger(service));
        const isBilledUpfront = serviceModel.isBilledUpfront(service);
        const isSkipBillingOnAcceptance = serviceModel.isSkipBillingOnAcceptance(service);
        const isRecurring = serviceBillingModel.isRecurring(serviceModel.billingTrigger(service));
        const isOnDemand = serviceBillingModel.isOnDemand(serviceModel.billingTrigger(service));
        const isPaused = serviceModel.isPaused(service);
        const isAwaitingApproval = serviceModel.isAwaitingApproval(service.status);
        const hasFinancialCentsIntegration = serviceModel.hasFinancialCentsIntegration(service);
        const hasKarbonIntegration = serviceModel.hasKarbonIntegration(service);
        const startBillingAt = serviceModel.startBillingAt(service);
        let prorateIsDisabled;
        const canProrate = !(isBilledUpfront && isSkipBillingOnAcceptance) && !isPaused || !!startBillingAt;
        return compact([
            isFreshlyAddedOrDraft && isAwaitingApproval && isOngoing && canProrate && {
                type: AdvancedSetting.Prorate,
                label: settingsTexts.prorate.label(),
                info: settingsTexts.prorate.info(),
                disabled: prorateIsDisabled,
                disabledTooltip: settingsTexts.prorate.disabledTooltip(),
                optionName: optionNamesTexts.firstPaymentProration()
            },
            isAwaitingApproval && !isApprovedRelationship && {
                type: AdvancedSetting.Optional,
                label: settingsTexts.optional.label(),
                info: settingsTexts.optional.info(),
                optionName: optionNamesTexts.optionalService()
            },
            isAwaitingApproval && (isRecurring || isOnDemand) && {
                type: AdvancedSetting.LimitCharges,
                label: settingsTexts.limitCharges.label(),
                info: settingsTexts.limitCharges.info(),
                optionName: optionNamesTexts.numberOfBillingCycles()
            },
            serviceModel.supportsPriceIncrease(service) && {
                type: AdvancedSetting.AutoPriceIncrease,
                label: settingsTexts.autoPriceIncrease.label(),
                info: settingsTexts.autoPriceIncrease.info(),
                optionName: optionNamesTexts.annualPriceIncrease()
            },
            !isApprovedRelationship && {
                type: AdvancedSetting.LimitedTimeDiscountOffer,
                label: settingsTexts.limitedTimeDiscountOffer.label(),
                disabled: !service.cost.discount,
                info: settingsTexts.limitedTimeDiscountOffer.info(),
                disabledTooltip: settingsTexts.limitedTimeDiscountOffer.disabledTooltip(),
                optionName: optionNamesTexts.conditionalDiscount()
            },
            isAwaitingApproval && serviceBillingModel.isRecurring(service.billing) && {
                type: AdvancedSetting.LimitedTimeDiscountOfferForAgreement,
                label: settingsTexts.expireDiscountAfterPeriod.label(),
                disabled: !service.cost.discount,
                info: settingsTexts.expireDiscountAfterPeriod.info(),
                disabledTooltip: settingsTexts.expireDiscountAfterPeriod.disabledTooltip(),
                optionName: optionNamesTexts.discountExpiration()
            },
            showQboIntegration && {
                type: AdvancedSetting.QboClasses,
                label: settingsTexts.qboClasses.label({
                    accountingName
                }),
                info: settingsTexts.qboClasses.info({
                    accountingName
                }),
                disabled: !allowToCustomizeQboClasses || !isLoadingQboClasses && qboClasses.length === 0,
                disabledTooltip: advancedSettingsTexts.qboClassDisabledTooltip({
                    accountingName
                }),
                optionName: optionNamesTexts.quickBooksOnlineClassOverride()
            },
            showFinancialCentsIntegration && !isApprovedRelationship && {
                type: AdvancedSetting.FinancialCents,
                label: settingsTexts.financialCents.label(),
                info: settingsTexts.financialCents.info(),
                disabled: !hasFinancialCentsIntegration,
                disabledTooltip: settingsTexts.financialCents.disabledTooltip(),
                optionName: optionNamesTexts.financialCentsAssigneeOverride()
            },
            showKarbonIntegration && !isApprovedRelationship && {
                type: AdvancedSetting.Karbon,
                label: settingsTexts.karbon.label(),
                info: settingsTexts.karbon.info(),
                disabled: !hasKarbonIntegration || !service.integrations.karbonIntegratedIds().length,
                disabledTooltip: settingsTexts.karbon.disabledTooltip(),
                optionName: optionNamesTexts.karbonAssigneeOverride()
            },
            serviceCostModel.isVariablePrice(service.cost) && {
                type: AdvancedSetting.PreapprovedAmount,
                label: settingsTexts.preapprovedAmount.label(),
                info: settingsTexts.preapprovedAmount.info(),
                optionName: optionNamesTexts.preapprovedAmount()
            }
        ]);
    }, [
        isFreshlyAdded,
        isApprovedRelationship,
        service,
        now,
        effectiveDate
    ]);
};
